import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Gift, Mail, Truck, Loader2, AlertCircle, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import api from '../services/api';

const GiftVoucherSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading');
  const [voucherData, setVoucherData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await api.get(`/gift-vouchers/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setVoucherData(data);
        } else if (pollCount < 10) {
          // Keep polling
          setPollCount(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        } else {
          setStatus('pending');
          setVoucherData(data);
        }
      } catch (err) {
        console.error('Error checking status:', err);
        if (pollCount < 10) {
          setPollCount(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkStatus();
  }, [sessionId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Processing Your Payment</h2>
            <p className="text-slate-600">Please wait while we confirm your purchase...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <SEO title="Error | Gift Voucher" />
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Something Went Wrong</h2>
            <p className="text-slate-600 mb-6">
              We couldn&apos;t verify your payment. If you were charged, please contact us and we&apos;ll sort it out.
            </p>
            <div className="space-y-3">
              <Link to="/gift-vouchers">
                <Button className="w-full">Try Again</Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="w-full">Contact Us</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-16" data-testid="voucher-success-page">
      <SEO title="Thank You! | Gift Voucher Purchase" />
      
      <div className="max-w-2xl mx-auto px-4">
        <Card className="overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white text-center">
            <div className="bg-white/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-12 w-12" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Thank You!</h1>
            <p className="text-green-100">Your gift voucher purchase was successful</p>
          </div>

          <CardContent className="p-8">
            {/* Voucher Code Display */}
            {voucherData?.voucher_code && (
              <div className="bg-slate-100 rounded-xl p-6 text-center mb-8">
                <p className="text-sm text-slate-600 mb-2">Voucher Code</p>
                <p className="text-3xl font-mono font-bold text-blue-600 tracking-wider" data-testid="voucher-code">
                  {voucherData.voucher_code}
                </p>
              </div>
            )}

            {/* What Happens Next */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900">What Happens Next?</h2>
              
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                <div className="bg-blue-100 rounded-full p-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Email Confirmation</h3>
                  <p className="text-sm text-slate-600">
                    You&apos;ll receive an email confirmation shortly with all the details.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg">
                <div className="bg-green-100 rounded-full p-2">
                  <Gift className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Voucher Delivery</h3>
                  <p className="text-sm text-slate-600">
                    The beautifully designed voucher will be sent to the recipient via their chosen delivery method.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                <div className="bg-amber-100 rounded-full p-2">
                  <Truck className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Redeem the Voucher</h3>
                  <p className="text-sm text-slate-600">
                    The recipient can quote the voucher code when booking to apply the credit to their hire.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8 pt-8 border-t border-slate-200 text-center">
              <p className="text-slate-600 mb-4">
                Want to browse our motorhomes or buy another voucher?
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button variant="outline" className="w-full sm:w-auto">
                    <Home className="mr-2 h-4 w-4" />
                    Back to Home
                  </Button>
                </Link>
                <Link to="/motorhomes">
                  <Button className="w-full sm:w-auto">
                    View Motorhomes
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GiftVoucherSuccess;
